import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { Button, buttonVariants, Card, CardContent } from "@rumbledirect/ui-v2";
import { useCurrentUser } from "@/currentUser/CurrentUserProvider";
import { hasPermission } from "@/currentUser/permissions";
import { DossierCreateSkeleton } from "@/dossiers/DossierCreateSkeleton";
import { DossierCreateEditor } from "@/dossiers/DossierCreateEditor";
import { DEFAULT_DOSSIERS_SEARCH } from "@/dossiers/dossiersSearch";
import type { DossierIntakeTab } from "@/dossiers/dossierIntakeDraft";
import type { DossierCreateFormValues } from "@/dossiers/dossierCreateForm";
import {
  DOSSIER_CONCEPT_PERMISSION,
  DOSSIER_CREATE_PERMISSION,
  DOSSIER_MEDIA_CREATE_PERMISSION,
} from "@/dossiers/dossierPermissions";
import {
  DOSSIER_INTAKE_FORM_TYPE,
  mapDossierCreatePrefillToSurveyData,
} from "@/dossiers/dossierIntakeSurvey";
import { useConcept } from "@/dossiers/useConcept";
import { useFormConfiguration } from "@/dossiers/useFormConfiguration";
import type { DossierMediaUpload } from "@/dossiers/useUploadDossierMedia";
import { useGetAdminCompaniesBrief } from "@/lib/api/generated/company/company";
import { registerSurveyExtensions } from "@/survey/registerSurveyExtensions";
import { Model } from "survey-core";
import { useMemo, useState } from "react";

registerSurveyExtensions();
interface DossierNewSearch {
  readonly conceptId?: string;
  readonly tab?: DossierIntakeTab;
}
export const Route = createFileRoute("/_authenticated/dossiers_/new")({
  validateSearch: (search: Record<string, unknown>): DossierNewSearch => ({
    conceptId:
      typeof search.conceptId === "string" && search.conceptId
        ? search.conceptId
        : undefined,
    tab:
      search.tab === "details" || search.tab === "damage"
        ? search.tab
        : undefined,
  }),
  component: DossierCreatePage,
});
function DossierCreatePage() {
  const { conceptId, tab } = Route.useSearch();
  const location = useRouter().state.location;
  const user = useCurrentUser();
  const canCreate = hasPermission(user, DOSSIER_CREATE_PERMISSION);
  const canSaveConcept = hasPermission(user, DOSSIER_CONCEPT_PERMISSION);
  // Real uploads require both the binary-upload and dossier-link permissions.
  const mayUpload =
    hasPermission(user, DOSSIER_MEDIA_CREATE_PERMISSION) &&
    hasPermission(user, "admin_media_create");
  const form = useFormConfiguration(DOSSIER_INTAKE_FORM_TYPE);
  const concept = useConcept(canSaveConcept ? conceptId : undefined);
  // Retained for workflow hand-offs. No PII in URLs.
  const prefill = (
    location.state as
      | { dossierCreatePrefill?: Partial<DossierCreateFormValues> }
      | undefined
  )?.dossierCreatePrefill;
  const companies = useGetAdminCompaniesBrief(undefined, {
    query: { enabled: Boolean(prefill?.opdrachtgeverCompanyId) },
  });
  const [companiesSkipped, setCompaniesSkipped] = useState(false);
  const [files, setFiles] = useState<readonly DossierMediaUpload[]>([]);
  const prefilledData = useMemo(
    () =>
      form.schema && prefill
        ? mapDossierCreatePrefillToSurveyData(
            prefill,
            new Model(form.schema as Record<string, unknown>),
            companies.data?.member ?? [],
          )
        : {},
    [form.schema, prefill, companies.data],
  );
  if (!canCreate)
    return (
      <Card role="alert" className="p-6">
        Je hebt geen toegang om een dossier aan te maken.
      </Card>
    );
  if (conceptId && !canSaveConcept)
    return (
      <Card role="alert" className="mx-auto max-w-3xl p-8">
        Je hebt geen toegang tot dit concept.
      </Card>
    );
  if (
    form.isLoading ||
    (conceptId && canSaveConcept && concept.isPending) ||
    (prefill?.opdrachtgeverCompanyId && companies.isPending)
  )
    return <DossierCreateSkeleton tab={tab} />;
  const error =
    (form.isError && form.schema === undefined) ||
    (conceptId && concept.isError && !concept.data) ||
    (prefill?.opdrachtgeverCompanyId &&
      companies.isError &&
      !companies.data &&
      !companiesSkipped);
  if (error)
    return (
      <Card role="alert" className="mx-auto max-w-3xl">
        <CardContent className="space-y-4 p-8">
          <h1 className="text-xl font-semibold">
            Het formulier kon niet worden geladen
          </h1>
          <p className="text-sm text-muted-foreground">
            {conceptId && concept.isError
              ? "Het concept bestaat niet meer of kon niet worden opgehaald."
              : "Probeer het opnieuw. Je opgeslagen concept blijft bewaard."}
          </p>
          <Button
            variant="outline"
            onClick={() => {
              void form.refetch();
              if (conceptId) void concept.refetch();
              if (prefill?.opdrachtgeverCompanyId) void companies.refetch();
            }}
          >
            Opnieuw proberen
          </Button>
          {prefill?.opdrachtgeverCompanyId && companies.isError && (
            <Button variant="ghost" onClick={() => setCompaniesSkipped(true)}>
              Ga verder zonder opdrachtgever
            </Button>
          )}
          <Link
            to="/dossiers"
            search={DEFAULT_DOSSIERS_SEARCH}
            className={buttonVariants({ variant: "ghost" })}
          >
            Terug naar dossiers
          </Link>
        </CardContent>
      </Card>
    );
  if (!form.schema)
    return (
      <Card className="mx-auto max-w-3xl p-8">
        Er is geen intakeformulier geconfigureerd voor deze omgeving.
      </Card>
    );
  const schema = form.schema as Record<string, unknown>;
  return (
    <DossierCreateEditor
      key={conceptId ?? "new"}
      schema={schema}
      data={{
        ...prefilledData,
        ...concept.data?.data,
      }}
      concept={concept.data}
      initialTab={tab}
      canSaveConcept={canSaveConcept}
      mayUpload={mayUpload}
      files={files}
      onFilesChange={setFiles}
    />
  );
}
